import React, { useState, useEffect } from 'react';
import { AppShell } from '../components/layout';
import { Avatar } from '../components/ui';
import { useAppContext } from '../store/AppContext';
import { useSocket } from '../hooks/useSocket';
import { ChannelSidebar } from './workspace/ChannelSidebar';
import { MessageFeed } from './workspace/MessageFeed';
import { ThreadPanel } from './workspace/ThreadPanel';
import { TeamsPanel } from './workspace/TeamsPanel';
import { TaskBoard } from './workspace/TaskBoard';
import { SettingsPage } from './SettingsPage';
import type { Message } from '../api/types';

const PROJECT_COLORS = [
  '#4F46E5', '#059669', '#DC2626', '#D97706', '#7C3AED', '#0891B2',
];

function colorForProject(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return PROJECT_COLORS[Math.abs(hash) % PROJECT_COLORS.length];
}

type WorkspaceView = 'channel' | 'teams' | 'tasks' | 'settings';

interface Props {
  onBack: () => void;
  onLogout: () => void;
}

export const WorkspacePage: React.FC<Props> = ({ onBack, onLogout }) => {
  const {
    projects,
    activeProjectId,
    setActiveProjectId,
    activeChannelId,
    setActiveChannelId,
    setMessages,
  } = useAppContext();

  const token = localStorage.getItem('auth_token');
  const { joinChannel } = useSocket(token);

  const [view, setView] = useState<WorkspaceView>('channel');
  const [threadMessage, setThreadMessage] = useState<Message | null>(null);

  // Default to first project if none selected
  useEffect(() => {
    if (!activeProjectId && projects.length > 0) {
      setActiveProjectId(projects[0].id);
    }
  }, [projects, activeProjectId, setActiveProjectId]);

  const handleSelectProject = (projectId: string) => {
    setActiveProjectId(projectId);
    setActiveChannelId(null);
    setMessages([]);
    setView('channel');
    setThreadMessage(null);
  };

  const handleSelectChannel = (channelId: string) => {
    setActiveChannelId(channelId);
    setMessages([]);
    setThreadMessage(null);
    setView('channel');
    joinChannel(channelId);
  };

  const activeProject = projects.find((p) => p.id === activeProjectId);

  const rail = (
    <>
      {projects.map((p) => {
        const initials = p.name
          .split(/\s+/)
          .map((w) => w[0])
          .join('')
          .toUpperCase()
          .slice(0, 2);
        const color = colorForProject(p.name);
        return (
          <button
            key={p.id}
            onClick={() => handleSelectProject(p.id)}
            title={p.name}
            style={{
              width: 40,
              height: 40,
              borderRadius: activeProjectId === p.id ? 12 : 20,
              background: color,
              border:
                activeProjectId === p.id
                  ? '2px solid white'
                  : '2px solid transparent',
              cursor: 'pointer',
              color: 'white',
              fontWeight: 700,
              fontSize: 13,
              transition: 'border-radius 0.2s',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {initials}
          </button>
        );
      })}
      <div style={{ flex: 1 }} />
      {/* Back to projects */}
      <button
        onClick={onBack}
        title="All projects"
        style={{
          width: 40,
          height: 40,
          borderRadius: 20,
          background: 'rgba(255,255,255,0.12)',
          border: '2px dashed rgba(255,255,255,0.3)',
          cursor: 'pointer',
          color: 'rgba(255,255,255,0.7)',
          fontWeight: 700,
          fontSize: 16,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ⌂
      </button>
    </>
  );

  const sidebar = activeProjectId ? (
    <ChannelSidebar
      projectId={activeProjectId}
      activeChannelId={activeChannelId}
      onSelectChannel={handleSelectChannel}
      onViewTeams={() => setView('teams')}
      onViewTasks={() => setView('tasks')}
      onViewSettings={() => setView('settings')}
      activeView={view}
    />
  ) : (
    <div style={{ padding: 16, color: 'var(--text-muted)', fontSize: 13 }}>
      Select a project
    </div>
  );

  let mainContent: React.ReactNode = null;

  if (!activeProjectId) {
    mainContent = (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          color: 'var(--text-muted)',
        }}
      >
        Select a project from the sidebar
      </div>
    );
  } else if (view === 'teams') {
    mainContent = <TeamsPanel projectId={activeProjectId} />;
  } else if (view === 'tasks') {
    mainContent = <TaskBoard projectId={activeProjectId} />;
  } else if (view === 'settings') {
    mainContent = <SettingsPage onLogout={onLogout} />;
  } else if (view === 'channel') {
    if (!activeChannelId) {
      mainContent = (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            height: '100%',
            color: 'var(--text-muted)',
            gap: 8,
          }}
        >
          <div style={{ fontSize: 32 }}>💬</div>
          <div style={{ fontWeight: 600 }}>
            {activeProject?.name ?? 'Project'}
          </div>
          <div style={{ fontSize: 13 }}>
            Select a channel from the sidebar to start chatting
          </div>
        </div>
      );
    } else {
      mainContent = (
        <MessageFeed
          channelId={activeChannelId}
          onOpenThread={(msg) => setThreadMessage(msg)}
        />
      );
    }
  }

  const rightPanel =
    threadMessage ? (
      <ThreadPanel
        parentMessage={threadMessage}
        onClose={() => setThreadMessage(null)}
      />
    ) : undefined;

  return (
    <AppShell
      rail={rail}
      sidebar={sidebar}
      main={mainContent ?? <div />}
      rightPanel={rightPanel}
      showRightPanel={!!threadMessage}
    />
  );
};
